import { AnyAction } from '@reduxjs/toolkit'
import { addNotificationToQueue } from 'components/Notifications'
import { AppEvent, logAppEvent } from 'lib/analytics'
import { disputedRegionsCountryCodes, mapConfig } from 'lib/config'
import { i18n } from 'lib/i18n'
import { createPlatform } from 'map/platform'
import {
  createAutosuggestRequestParams,
  createDiscoverRequestParams,
  createReverseGeocodeRequestParams,
  SearchService,
  SearchServiceResultItem,
} from 'map/services'
import { Epic, StateObservable } from 'redux-observable'
import { EMPTY, Observable, of } from 'rxjs'
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators'
import {
  createSerializedRecentItem,
  getRecentList,
  RecentItemType,
  SerializedRecentItem,
  spliceInRecentList,
  updateRecentList,
} from 'utils/storage'

import { getDisputedRegionIso3 } from '../../utils/politicalViews'
import { ActionParamPlaceHolder } from '../model'
import {
  fromSearchServiceResultItemToSearchStateListItem,
  fromSearchStateListItemToRecentItem,
  fromSerializedRecentItemToSearchStateListItem,
  SearchStateListItem,
  SearchStateListType,
} from '../model/search'
import { RootState } from '../reducer'
import {
  clearRecentItemList,
  fetchAutosuggestItemList,
  fetchBrowseItemList,
  fetchLocationAddress,
  fetchRecentItemList,
  fetchSearchItemList,
  setAutosuggestItemList,
  setBrowseItemList,
  setError,
  setIsLoading,
  setLastUpdatedList,
  setQuery,
  setRecentItem,
  setRecentItemList,
  setReverseGeocodeItemList,
  setSearchItemList,
} from '../reducer/search'

// TODO this needs to be moved inside config apis
const searchService = createPlatform().getSearchService() as unknown as SearchService

export const fetchSearchItemListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchSearchItemList.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      if (action.payload.query === ActionParamPlaceHolder.NotDefined) {
        return EMPTY
      }

      const query =
        action.payload.query === '' || action.payload.query === ActionParamPlaceHolder.FromState
          ? state.search.query
          : action.payload.query
      const at =
        action.payload.at === ActionParamPlaceHolder.NotDefined
          ? undefined
          : (action.payload.at as H.geo.IPoint)
      const searchIn =
        action.payload.searchIn === ActionParamPlaceHolder.NotDefined
          ? undefined
          : (action.payload.searchIn as string)
      const lang = state.app.language
      const iso3CountryCode =
        state.user.countryCode !== null
          ? getDisputedRegionIso3(state.user.countryCode, disputedRegionsCountryCodes)
          : undefined
      const countryCode = state.user.countryCode
      const unit = state.app.unit
      return new Observable<SearchStateListItem[]>((observer) => {
        if (query === '') {
          observer.next([])
          observer.complete()
        } else {
          searchService.discover(
            createDiscoverRequestParams(query, lang, at, searchIn, iso3CountryCode),
            ({ items }) => {
              const resultItems = items.map((item: SearchServiceResultItem) =>
                fromSearchServiceResultItemToSearchStateListItem(
                  item,
                  { countryCode, unit },
                  state.user.currentPosition.coordinates
                )
              )
              observer.next(resultItems)
              observer.complete()
            },
            (error) => {
              console.error('"/discover" request error', error)
              observer.error(error)
            }
          )
        }
      }).pipe(
        switchMap((items: SearchStateListItem[]) => {
          const actionList: AnyAction[] = [setQuery(query), setSearchItemList(items)]

          if (state.search.lastUpdatedList !== SearchStateListType.Search) {
            actionList.push(setLastUpdatedList(SearchStateListType.Search))
          }

          return of(...actionList)
        }),
        catchError((error: string) => of(setError(error)))
      )
    })
  )

export const fetchAutosuggestItemListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchAutosuggestItemList.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const query = action.payload.query !== undefined ? action.payload.query : ''
      const at =
        action.payload.at !== undefined
          ? action.payload.at
          : (mapConfig.geolocation.defaultPosition as H.geo.IPoint)
      const lang = state.app.language
      const iso3CountryCode =
        state.user.countryCode !== null
          ? getDisputedRegionIso3(state.user.countryCode, disputedRegionsCountryCodes)
          : undefined
      const countryCode = state.user.countryCode
      const unit = state.app.unit

      return new Observable<SearchStateListItem[]>((observer) => {
        searchService.autosuggest(
          createAutosuggestRequestParams(query, at, lang, iso3CountryCode),
          ({ items }) => {
            const resultItems = items.map((item: SearchServiceResultItem) =>
              fromSearchServiceResultItemToSearchStateListItem(
                item,
                { countryCode, unit },
                state.user.currentPosition.coordinates
              )
            )
            observer.next(resultItems)
            observer.complete()
          },
          (error) => {
            console.error('"/autosuggest" request error', error)
            observer.error(error)
          }
        )
      }).pipe(
        switchMap((items) => {
          const actionList: AnyAction[] = [setQuery(query), setAutosuggestItemList(items)]

          if (state.search.lastUpdatedList !== SearchStateListType.Autosuggest) {
            actionList.push(setLastUpdatedList(SearchStateListType.Autosuggest))
          }

          return of(...actionList)
        }),
        catchError((error: string) => of(setError(error)))
      )
    })
  )

export const setRecentItemEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(setRecentItem.match),
    withLatestFrom(state$),
    map(([action, state]) => {
      const countryCode = state.user.countryCode
      const unit = state.app.unit

      const recentItem = action.payload.item
        ? fromSearchStateListItemToRecentItem(action.payload.item)
        : undefined
      const consent = localStorage.getItem('CONSENT')
      const itemResultType: RecentItemType =
        action.payload.type === undefined &&
        (recentItem?.resultType === undefined || recentItem.resultType === '')
          ? RecentItemType.freeText
          : action.payload.type !== undefined
          ? action.payload.type
          : recentItem !== undefined &&
            (recentItem.resultType !== undefined || recentItem.resultType !== '')
          ? (recentItem.resultType as RecentItemType)
          : RecentItemType.freeText
      const serializedRecentItem = createSerializedRecentItem(
        itemResultType,
        action.payload.query,
        recentItem
      )
      const recentList: SerializedRecentItem[] = getRecentList()

      // If none of those values exist there is actually no recent item to update
      if (action.payload.query !== undefined || recentItem !== undefined) {
        // TODO temporary solution need to revisit for in memory updating list and when consent become part of state.
        if (consent === 'ACCEPTED') {
          const updatedRecentList = spliceInRecentList(serializedRecentItem, recentList)
          updateRecentList(updatedRecentList)
          const updatedSearchStateRecentList = updatedRecentList.map((item) =>
            fromSerializedRecentItemToSearchStateListItem(
              item,
              { countryCode, unit },
              state.user.currentPosition.coordinates
            )
          )
          return setRecentItemList(
            updatedSearchStateRecentList.slice(0, Number(mapConfig.services.search.recent.limit))
          )
        }
      }

      return setRecentItemList(state.search.recentItemList)
    })
  )

export const clearRecentItemListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(clearRecentItemList.match),
    withLatestFrom(state$),
    switchMap(([action$]) => {
      updateRecentList([])
      void logAppEvent(AppEvent.HistoryCleared, { flow: action$.payload.flow })

      return of(
        addNotificationToQueue({
          content: i18n('account.menu.notification.history-cleared'),
          duration: 3,
          icon: 'checkmark',
          iconStyle: 'solid',
          inline: true,
          variant: 'confirmation',
        }),
        setRecentItemList([])
      )
    })
  )

export const fetchRecentListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchRecentItemList.match),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      // eslint-disable-next-line no-debugger
      // debugger
      const countryCode = state.user.countryCode
      const unit = state.app.unit
      const stateRecentList = state.search.recentItemList
      const shouldSerialize =
        state.user.currentPosition.coordinates !== null &&
        stateRecentList.length > 0 &&
        stateRecentList[0].distanceLabel === undefined
      if (stateRecentList.length > 0 && !shouldSerialize) {
        const actionList: AnyAction[] = [
          setRecentItemList(
            stateRecentList.slice(0, Number(mapConfig.services.search.recent.limit))
          ),
        ]
        if (state.search.lastUpdatedList !== SearchStateListType.Recent) {
          actionList.push(setLastUpdatedList(SearchStateListType.Recent))
        }
        return of(...actionList)
      }
      const storageRecentList = getRecentList().map((item) =>
        fromSerializedRecentItemToSearchStateListItem(
          item,
          { countryCode, unit },
          state.user.currentPosition.coordinates
        )
      )

      const actionList: AnyAction[] = [
        setRecentItemList(
          storageRecentList.slice(0, Number(mapConfig.services.search.recent.limit))
        ),
      ]

      if (state.search.lastUpdatedList !== SearchStateListType.Recent) {
        actionList.push(setLastUpdatedList(SearchStateListType.Recent))
      }

      return of(...actionList)
    })
  )

// TODO: This should not be here, this is part of the routing page
export const fetchReverseGeocodeItemListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchLocationAddress.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const countryCode = state.user.countryCode
      const unit = state.app.unit
      const at = action.payload.at as H.geo.IPoint
      const lang = state.app.language
      return new Observable<SearchStateListItem[]>((observer) => {
        searchService.reverseGeocode(
          createReverseGeocodeRequestParams(at, lang, action.payload.type),
          ({ items }) => {
            const resultItems = items.map((item: SearchServiceResultItem) =>
              fromSearchServiceResultItemToSearchStateListItem(
                item,
                { countryCode, unit },
                state.user.currentPosition.coordinates
              )
            )
            observer.next(resultItems)
            observer.complete()
          },
          (error) => {
            console.error('"/reversegeocode" request error', error)
            observer.error(error)
          }
        )
      }).pipe(
        switchMap((items: SearchStateListItem[]) => {
          const actionList: AnyAction[] = [setReverseGeocodeItemList(items), setIsLoading(false)]

          return of(...actionList)
        }),
        catchError((error: string) => of(setError(error), setIsLoading(false)))
      )
    })
  )

export const fetchBrowseItemListEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchBrowseItemList.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const countryCode = state.user.countryCode
      const unit = state.app.unit
      const at = action.payload.at as H.geo.IPoint
      const limit = action.payload.limit
      const name = action.payload.name
      const categories = action.payload.categories.join(',')
      return new Observable<SearchStateListItem[]>((observer) => {
        searchService.browse(
          {
            at: `${at.lat},${at.lng}`,
            categories,
            limit: limit !== null && limit !== undefined ? limit.toString() : '1',
          },
          ({ items }) => {
            const pdcItem = items.find((item) => item.title === name) || items[0]
            const resultItems = [pdcItem].map((item: SearchServiceResultItem) =>
              fromSearchServiceResultItemToSearchStateListItem(
                item,
                { countryCode, unit },
                state.user.currentPosition.coordinates
              )
            )
            observer.next(resultItems)
            observer.complete()
          },
          (error) => {
            console.error('"/fetchBrowseItemList" request error', error)
            observer.error(error)
          }
        )
      }).pipe(
        switchMap((items: SearchStateListItem[]) => {
          const actionList: AnyAction[] = [setBrowseItemList(items), setIsLoading(false)]

          return of(...actionList)
        }),
        catchError((error: string) => of(setError(error), setIsLoading(false)))
      )
    })
  )
